// components/playground/sections/Scenario/ScenarioModal.jsx
import React from 'react';
import Modal from '../../../common/Modal';
import ScenarioInfoCard from './ScenarioInfoCard';
import ChangesBreakdownSection from './ChangesBreakdownSection';
import ActualDataView from './ActualDataView';
import ScenarioSidebar from './ScenarioSidebar';
import { useScenarioModalState } from '../../../../hooks/playground/scenario/useScenarioModalState';
import { useScenarioActions } from '../../../../hooks/playground/scenario/useScenarioActions';
import { getButtonClass, MODAL_THEME } from '../../../../config/theme.config';

const ScenarioModal = ({
  isOpen,
  onClose,
  scenarios,
  scenariosLoaded,
  onLoadScenarios,
  currentScenarioId,
  currentScenarioName,
  hasUnsavedChanges,
  selectedYear,
  currentYear,
  currentMonth,
  averagingMethod,
  adjustments,
  customItems,
  lineItems = [],
  
  // Actions
  onLoadScenario,
  onDeleteScenario,
  onNewScenario,
  onLoadActual,
  onSaveAsNew,
  onUpdateScenario,
  
  // Loading states
  isLoadingScenario,
  isSavingScenario,

  defaultScenarioId,
  onSetAsDefault,
}) => {
  // State management hook
  const {
    editMode,
    setEditMode,
    editedName,
    setEditedName,
    editedDescription,
    setEditedDescription,
    isSaving,
    setIsSaving,
    currentScenario,
    isActualView,
    adjustmentCount,
    totalAdjustmentCount,
    historicalAdjustmentCount,
    customItemCount,
  } = useScenarioModalState({
    scenarios,
    currentScenarioId,
    adjustments,
    customItems,
    currentYear,
    currentMonth,
  });

  // Actions hook
  const {
    handleSave,
    handleDelete,
    handleLoadScenarioClick,
    handleLoadActualClick,
    handleNewScenarioClick,
    handleCancelEdit,
    handleSaveAsNew,
  } = useScenarioActions({
    currentScenarioId,
    currentScenario,
    editedName,
    editedDescription,
    selectedYear,
    averagingMethod,
    adjustments,
    customItems,
    hasUnsavedChanges,
    setEditMode,
    setEditedName,
    setEditedDescription,
    setIsSaving,
    onDeleteScenario,
    onLoadScenario,
    onLoadActual,
    onNewScenario,
    onSaveAsNew,
    onUpdateScenario,
  });
  
  // Utility function
  const formatRelativeTime = (timestamp) => {
    if (!timestamp) return 'Never';
    
    const now = new Date();
    const then = new Date(timestamp);
    const diffMs = now - then;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 7) return `${diffDays}d ago`;
    
    return then.toLocaleDateString();
  };

  // Custom footer content
  const footer = (
    <div className="flex items-center justify-between w-full">
      <div className={MODAL_THEME.text.muted}>
        {scenarios.length} saved scenario{scenarios.length !== 1 ? 's' : ''}
      </div>
      <button
        onClick={onClose}
        className={getButtonClass('secondary')}
      >
        Close
      </button>
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Scenario Manager"
      subtitle="Manage and switch between planning scenarios"
      footer={footer}
      size="xl"
      bodyClassName="p-0 overflow-hidden" // ✅ No padding, no overflow
    >
      {/* ✅ Fixed height container with flex layout */}
      <div className="flex h-[calc(85vh-140px)] min-h-[500px]">
        
        {/* ✅ Main Content Area - Scrollable */}
        <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
          <div className="flex-1 overflow-y-auto overflow-x-hidden p-6">
            {!scenariosLoaded ? (
              // Empty State - Load Scenarios
              <div className="flex flex-col items-center justify-center py-12">
                <div className={MODAL_THEME.text.light}>
                  <svg className="w-16 h-16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <h3 className={`text-lg ${MODAL_THEME.text.heading} mb-2`}>
                  Load Your Scenarios
                </h3>
                <p className={`text-sm ${MODAL_THEME.text.muted} mb-6 text-center max-w-md`}>
                  Click below to load your saved scenarios from the database
                </p>
                <button
                  onClick={onLoadScenarios}
                  className={getButtonClass('primary')}
                >
                  📥 Load Scenarios
                </button>
              </div>
            ) : isActualView ? (
              // Actual Data View
              <ActualDataView onCreateFirstScenario={handleNewScenarioClick} />
            ) : (
              // Scenario View
              <div className="space-y-6">
                {/* Current Scenario Info Card */}
                <ScenarioInfoCard
                  currentScenario={currentScenario}
                  hasUnsavedChanges={hasUnsavedChanges}
                  editMode={editMode}
                  editedName={editedName}
                  editedDescription={editedDescription}
                  selectedYear={selectedYear}
                  averagingMethod={averagingMethod}
                  adjustmentCount={adjustmentCount}
                  historicalAdjustmentCount={historicalAdjustmentCount}
                  customItemCount={customItemCount}
                  isSaving={isSaving}
                  onEditModeChange={setEditMode}
                  onNameChange={setEditedName}
                  onDescriptionChange={setEditedDescription}
                  onSave={handleSave}
                  onCancel={handleCancelEdit}
                  formatRelativeTime={formatRelativeTime}
                />

                {/* Action Buttons */}
                {!editMode && (
                  <div className="flex items-center gap-3 flex-wrap">
                    {hasUnsavedChanges && (
                      <button
                        onClick={handleSaveAsNew}
                        className={getButtonClass('success')}
                      >
                        💾 Save as New
                      </button>
                    )}
                    {currentScenarioId && (
                      <>
                        {/* Set as Default Button */}
                        <button
                          onClick={() => {
                            if (window.confirm(`Set "${currentScenarioName}" as your default scenario?`)) {
                              onSetAsDefault(currentScenarioId);
                            }
                          }}
                          className={`${getButtonClass('secondary')} ${
                            defaultScenarioId === currentScenarioId ? 'ring-2 ring-yellow-500 bg-yellow-50' : ''
                          }`}
                        >
                          {defaultScenarioId === currentScenarioId ? '⭐ Default' : '☆ Set as Default'}
                        </button>
                        
                        <button
                          onClick={handleDelete}
                          className={getButtonClass('danger')}
                        >
                          🗑️ Delete
                        </button>
                      </>
                    )}
                  </div>
                )}

                {/* Changes Breakdown Section */}
                {!editMode && currentScenario && (
                  <ChangesBreakdownSection
                    currentScenario={currentScenario}
                    adjustmentCount={adjustmentCount}
                    totalAdjustmentCount={totalAdjustmentCount}
                    historicalAdjustmentCount={historicalAdjustmentCount}
                    customItemCount={customItemCount}
                    currentYear={currentYear}
                    currentMonth={currentMonth}
                    lineItems={lineItems}
                  />
                )}


              </div>
            )}
          </div>
        </div>

        {/* Right Sidebar - Only show if loaded */}
        {scenariosLoaded && (
          <ScenarioSidebar
            scenarios={scenarios}
            currentScenarioId={currentScenarioId}
            isActualView={isActualView}
            hasUnsavedChanges={hasUnsavedChanges}
            currentYear={currentYear}
            currentMonth={currentMonth}
            onLoadActual={handleLoadActualClick}
            onLoadScenario={handleLoadScenarioClick}
            onNewScenario={handleNewScenarioClick}
            formatRelativeTime={formatRelativeTime}
          />
        )}
      </div>
    </Modal>
  );
};

export default ScenarioModal;